import React, { memo, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  ActionList,
  ActionListItemProps,
  Avatar,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  Icon,
} from '@node-space/ui-components'
import { ConnectionStatusTypes, NetworkConnectionViewTypes } from 'constants/bvnkNetwork'
import { useProfileContext } from 'hooks/context/useProfileContext'
import WalletModal, { WalletAction } from 'pages/Wallets/components/Modal/WalletModal'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { BvnkNetworkConnection } from 'types/bvnkNetwork'
import { WalletTypes } from 'types/types'
import { PayActionBeneficiaryTypes } from 'types/wallets'
import { ConnectionConfirmationModal } from './ConnectionConfirmationModal'
import ConnectionsEmptyState from './ConnectionsEmptyState'

export interface ConnectionsTabProps {
  activeConnections: BvnkNetworkConnection[]
}

const ActiveConnections = ({ activeConnections }: ConnectionsTabProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { profile } = useProfileContext()

  const [isPayWalletModalOpen, setIsPayWalletModalOpen] = useState(false)
  const [isDeleteConnectionModalOpen, setIsDeleteConnectionModalOpen] = useState(false)
  const [connectionToDisconnect, setConnectionToDisconnect] = useState<BvnkNetworkConnection>(null)
  const [walletModalAction, setWalletModalAction] = useState<WalletAction>(null)

  const handleTransferClick = (connection: BvnkNetworkConnection) => {
    const walletAction: WalletAction = {
      key: 'pay',
      metadata: {
        pay: {
          beneficiaryOptionId: connection?.id,
          beneficiaryPaymentType: PayActionBeneficiaryTypes.NETWORK,
        },
      },
    }

    setWalletModalAction(walletAction)
    setIsPayWalletModalOpen(true)
  }

  const handleConnectionDisconnectClick = (connection: BvnkNetworkConnection) => {
    setConnectionToDisconnect(connection)
    setIsDeleteConnectionModalOpen(true)
  }

  const onModalClose = () => {
    setConnectionToDisconnect(null)
    setIsDeleteConnectionModalOpen(false)
  }

  const refreshWallets = async () => {
    await queryClient.invalidateQueries({ queryKey: reactQueryKeys.walletsV2(WalletTypes.FIAT) })
    await queryClient.invalidateQueries({
      queryKey: reactQueryKeys.totalBalance(profile?.currencyCode),
    })
  }

  const actionItemListItems: ActionListItemProps[] = useMemo(() => {
    return activeConnections?.map(connection => {
      return {
        transparent: false,
        testId: 'active-connection-list-item',
        id: connection?.id,
        key: connection?.id,
        Avatar: <Avatar name={connection?.details?.name} size="small" type="entity" />,
        title: connection?.details?.name,
        BodyPost: connection?.details?.description,
        ActionButton: (
          <Button
            variant="ghost"
            iconElement={<Icon name="CoinArrowOutCustom" size="small" />}
            onClick={() => handleTransferClick(connection)}
          >
            {t('bvnkNetwork.transfer')}
          </Button>
        ),
        CustomAction: (
          <DropdownMenu
            key={`context-menu-${connection.id}`}
            DropdownMenuItems={[
              <DropdownMenuItem
                key="edit"
                LeadingIcon={<Icon name="Prohibit" />}
                onClick={() => handleConnectionDisconnectClick(connection)}
              >
                {t('bvnkNetwork.disconnect')}
              </DropdownMenuItem>,
            ]}
          >
            <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
          </DropdownMenu>
        ),
      }
    })
  }, [activeConnections])

  if (!actionItemListItems?.length) {
    return (
      <ConnectionsEmptyState
        networkConnectionViewType={NetworkConnectionViewTypes.CONNECTIONS_VIEW}
      />
    )
  } else {
    return (
      <Box flexCol>
        <ActionList id="connections-list" items={actionItemListItems} />
        {isPayWalletModalOpen && (
          <WalletModal
            action={walletModalAction}
            setWalletModalAction={setWalletModalAction}
            refresh={refreshWallets}
            setShowWalletModal={setIsPayWalletModalOpen}
          />
        )}
        {isDeleteConnectionModalOpen && (
          <ConnectionConfirmationModal
            connection={connectionToDisconnect}
            connectionStatus={ConnectionStatusTypes.DISCONNECTED}
            onModalClose={onModalClose}
          />
        )}
      </Box>
    )
  }
}

export default memo(ActiveConnections)
